import { isValidSourceClass } from "./roster.js";
import type { Classroom, DrawHistoryEntry, Student } from "./types.js";

export type DrawState = NonNullable<Classroom["drawState"]>;
export type DrawAction =
  | { type: "draw"; studentId: string; entryId: string; drawnAt: string }
  | { type: "reset"; date: string }
  | { type: "clearHistory" }
  | { type: "mode"; mode: DrawState["mode"]; date: string };

export function hongKongDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Hong_Kong",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = (type: string) => parts.find((part) => part.type === type)?.value || "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function availableStudentIds(activeIds: string[], state: DrawState | undefined, date: string): string[] {
  const drawn = state?.mode === "daily" && state.lastDrawDate !== date ? [] : state?.drawnStudentIds || [];
  const used = new Set(drawn);
  return activeIds.filter((id) => !used.has(id));
}

export function updatedDrawState(
  state: DrawState | undefined,
  mode: DrawState["mode"],
  activeIds: string[],
  date: string,
  drawnId?: string,
): DrawState {
  const remaining = new Set(availableStudentIds(activeIds, state, date));
  const drawnStudentIds = activeIds.filter((id) => !remaining.has(id));
  if (drawnId && remaining.has(drawnId)) drawnStudentIds.push(drawnId);
  return { ...state, mode, drawnStudentIds, lastDrawDate: date };
}

export function applyDrawAction(classroom: Classroom, students: Student[], action: DrawAction): DrawState {
  const state = classroom.drawState;
  const mode = state?.mode || "daily";
  if (action.type === "clearHistory") {
    return { mode, drawnStudentIds: [], lastDrawDate: hongKongDate(), ...state, history: [] };
  }
  if (action.type === "reset") {
    return { ...state, mode, drawnStudentIds: [], lastDrawDate: action.date };
  }
  const active = students.filter((student) => student.active);
  const ids = active.map((student) => student.id);
  if (action.type === "mode") return updatedDrawState(state, action.mode, ids, action.date);
  if (!action.entryId.trim() || !Number.isFinite(Date.parse(action.drawnAt))) throw new Error("抽選記錄無效。");
  const previous = state?.history?.find((entry) => entry.id === action.entryId);
  if (previous) {
    if (previous.studentId !== action.studentId || previous.drawnAt !== action.drawnAt)
      throw new Error("抽選記錄重複。");
    return state!;
  }
  const date = hongKongDate(new Date(action.drawnAt));
  const student = active.find((item) => item.id === action.studentId);
  if (!student || !availableStudentIds(ids, state, date).includes(student.id))
    throw new Error("學生已不在本輪可抽名單，請重新抽選。");
  return {
    ...updatedDrawState(state, mode, ids, date, student.id),
    history: [
      ...(state?.history || []),
      {
        id: action.entryId,
        studentId: student.id,
        number: student.number,
        ...(student.sourceClass ? { sourceClass: student.sourceClass } : {}),
        name: student.name,
        drawnAt: action.drawnAt,
      },
    ],
  };
}

export function isValidDrawHistory(value: unknown): value is DrawHistoryEntry[] {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>();
  return value.every((entry) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      typeof entry.id !== "string" ||
      !entry.id.trim() ||
      ids.has(entry.id) ||
      typeof entry.studentId !== "string" ||
      !entry.studentId.trim() ||
      !isValidSourceClass(entry.sourceClass) ||
      !Number.isInteger(entry.number) ||
      entry.number < 1 ||
      typeof entry.name !== "string" ||
      !entry.name.trim() ||
      typeof entry.drawnAt !== "string" ||
      !Number.isFinite(Date.parse(entry.drawnAt))
    )
      return false;
    ids.add(entry.id);
    return true;
  });
}
